import React from "react";
import EditableText from "./EditableText";
import { useContent } from "../context/ContentContext";

export default function Projects() {
  const { content } = useContent();
  const projects = content?.projects;
  if (!projects) return null;
  const items = projects.items ?? [];

  return (
    <section
      id="projects"
      className="relative py-24 md:py-32 bg-[#F9F8F6] border-t border-[#E2E0D8]"
      data-testid="projects-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Section header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-24 items-start">
          <div className="md:col-span-7">
            <EditableText
              path="projects.overline"
              value={projects.overline}
              as="div"
              className="text-[#4A4F6B] uppercase text-xs tracking-[0.3em] font-semibold mb-6"
              testId="projects-overline"
            />
            <EditableText
              path="projects.heading"
              value={projects.heading}
              as="h2"
              multiline
              className="font-serif text-[#1A1D1A] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold text-balance"
              testId="projects-heading"
            />
          </div>
          <div className="md:col-span-4 md:col-start-9 md:pt-4">
            <EditableText
              path="projects.intro"
              value={projects.intro}
              as="p"
              multiline
              className="text-[#1A1D1A]/80 text-base md:text-lg leading-relaxed"
              testId="projects-intro"
            />
          </div>
        </div>

        {/* Project blocks */}
        <div className="space-y-24 md:space-y-32">
          {items.map((p, i) => (
            <ProjectBlock key={p.id} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProjectBlock({ project, index }) {
  const pathBase = `projects.items.${index}`;
  const hasTech = (project.technologies ?? []).length > 0;
  const groups = project.stat_groups ?? [];
  const hasGroups = groups.length > 0;
  const hasStats = (project.stats ?? []).length > 0;
  const isFirst = index === 0;

  return (
    <div data-testid={`project-block-${index + 1}`} id={index === 1 ? "projects-solar" : undefined}>
      {/* Hero card */}
      <article className="relative overflow-hidden rounded-2xl bg-[#0A0B1F] min-h-[520px] md:min-h-0 md:aspect-[21/9]">
        <img
          src={project.hero_image}
          alt={project.title}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Gradient overlay */}
        {isFirst ? (
          <>
            {/* Top-left dark gradient for title area */}
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background: "linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.5) 25%, rgba(0,0,0,0.2) 45%, transparent 65%)"
              }}
            />
            {/* Top-right dark gradient for stats area */}
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background: "linear-gradient(225deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.5) 25%, rgba(0,0,0,0.2) 45%, transparent 65%)"
              }}
            />
          </>
        ) : (
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-4/5 md:h-3/5 bg-gradient-to-t from-black/80 via-black/45 to-transparent"
          />
        )}

        {/* Content */}
        <div className={`relative z-10 h-full flex flex-col ${isFirst ? "justify-start pt-6 md:pt-8 lg:pt-10" : "justify-end"} p-5 md:p-10 lg:p-14`}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
            <div className="md:col-span-5 lg:col-span-4 max-w-md">
              <EditableText
                path={`${pathBase}.title`}
                value={project.title}
                as="h3"
                multiline
                className="font-serif text-[#F9F8F6] text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-tight font-semibold mb-3 md:mb-4 drop-shadow-[0_3px_18px_rgba(0,0,0,0.75)]"
                testId={`project-${index + 1}-title`}
              />
              <EditableText
                path={`${pathBase}.description`}
                value={project.description}
                as="p"
                multiline
                className="text-[#F9F8F6]/95 text-[13px] md:text-[14px] leading-[1.6] md:leading-[1.7] max-w-sm drop-shadow-[0_2px_12px_rgba(0,0,0,0.75)]"
                testId={`project-${index + 1}-description`}
              />
            </div>

            {hasGroups && (() => {
              const maxStats = Math.max(...groups.map((g) => (g.stats ?? []).length));
              return (
                <div className="md:col-span-6 lg:col-span-6 lg:col-start-7 grid grid-cols-2 gap-x-8 md:gap-x-12 gap-y-0 auto-rows-min">
                  {groups.map((g, gi) => (
                    <EditableText
                      key={`title-${gi}`}
                      path={`${pathBase}.stat_groups.${gi}.title`}
                      value={g.title}
                      as="h4"
                      className="font-serif text-[#F9F8F6] text-base md:text-lg font-semibold leading-tight mb-1.5 md:mb-2 drop-shadow-[0_2px_14px_rgba(0,0,0,0.75)]"
                      testId={`project-${index + 1}-stat-group-${gi + 1}-title`}
                    />
                  ))}
                  {Array.from({ length: maxStats }).map((_, si) =>
                    groups.map((g, gi) => {
                      const s = (g.stats ?? [])[si];
                      if (!s) return <div key={`empty-${gi}-${si}`} />;
                      return (
                        <div
                          key={`${gi}-${si}`}
                          className="pt-1 md:pt-1.5 pb-1.5 md:pb-2 border-t border-[#F9F8F6]/25"
                          data-testid={`project-${index + 1}-group-${gi + 1}-stat-${si + 1}`}
                        >
                          <EditableText
                            path={`${pathBase}.stat_groups.${gi}.stats.${si}.label`}
                            value={s.label}
                            as="div"
                            className="text-[#F9F8F6]/80 text-[9px] md:text-[10px] uppercase tracking-[0.18em] mb-1 whitespace-nowrap overflow-hidden text-ellipsis drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]"
                            testId={`project-${index + 1}-group-${gi + 1}-stat-${si + 1}-label`}
                          />
                          <EditableText
                            path={`${pathBase}.stat_groups.${gi}.stats.${si}.value`}
                            value={s.value}
                            as="div"
                            className="font-serif text-[#F9F8F6] text-base md:text-[17px] font-semibold leading-tight whitespace-nowrap drop-shadow-[0_2px_14px_rgba(0,0,0,0.75)]"
                            testId={`project-${index + 1}-group-${gi + 1}-stat-${si + 1}-value`}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })()}

            {!hasGroups && hasStats && (
              <div className="md:col-span-6 lg:col-span-4 lg:col-start-9 flex flex-col">
                {project.stats.map((s, si) => (
                  <div
                    key={si}
                    className="pt-2 md:pt-2.5 pb-2.5 md:pb-3 border-t border-[#F9F8F6]/25"
                    data-testid={`project-${index + 1}-stat-${si + 1}`}
                  >
                    <EditableText
                      path={`${pathBase}.stats.${si}.label`}
                      value={s.label}
                      as="div"
                      className="text-[#F9F8F6]/80 text-[9px] md:text-[10px] uppercase tracking-[0.18em] mb-1 whitespace-nowrap overflow-hidden text-ellipsis drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]"
                      testId={`project-${index + 1}-stat-${si + 1}-label`}
                    />
                    <EditableText
                      path={`${pathBase}.stats.${si}.value`}
                      value={s.value}
                      as="div"
                      className="font-serif text-[#F9F8F6] text-base md:text-[17px] font-semibold leading-tight whitespace-nowrap drop-shadow-[0_2px_14px_rgba(0,0,0,0.75)]"
                      testId={`project-${index + 1}-stat-${si + 1}-value`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </article>

      {/* Technologies subsection */}
      {hasTech && (
        <div className="mt-16 md:mt-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-10 md:mb-16 items-start">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span aria-hidden className="inline-block w-2 h-2 rounded-full bg-[#00025C]" />
                <span className="text-[#00025C] uppercase text-xs tracking-[0.3em] font-semibold">
                  Technologies
                </span>
              </div>
              {project.tech_heading && (
                <EditableText
                  path={`${pathBase}.tech_heading`}
                  value={project.tech_heading}
                  as="h4"
                  multiline
                  className="font-serif text-[#1A1D1A] text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight font-semibold text-balance"
                  testId={`project-${index + 1}-tech-heading`}
                />
              )}
            </div>
            <div className="md:col-span-5 md:col-start-8 md:pt-3">
              <EditableText
                path={`${pathBase}.tech_intro`}
                value={project.tech_intro}
                as="p"
                multiline
                className="text-[#1A1D1A]/80 text-sm md:text-[15px] leading-relaxed"
                testId={`project-${index + 1}-tech-intro`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {project.technologies.map((t, ti) => (
              <TechCard
                key={t.id}
                tech={t}
                pathBase={`${pathBase}.technologies.${ti}`}
                projectIndex={index}
                techIndex={ti}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TechCard({ tech, pathBase, projectIndex, techIndex }) {
  return (
    <article
      className="relative overflow-hidden rounded-2xl aspect-[5/4] md:aspect-[4/3] bg-[#0A0B1F] text-[#F9F8F6]"
      data-testid={`project-${projectIndex + 1}-tech-card-${techIndex + 1}`}
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(130% 90% at 18% 110%, rgba(130,160,230,0.38) 0%, rgba(70,90,170,0.22) 28%, rgba(30,35,80,0.12) 55%, rgba(10,11,31,1) 82%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(90% 55% at 85% 10%, rgba(180,200,255,0.12) 0%, rgba(10,11,31,0) 45%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-50 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.22 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
      <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-8">
        <EditableText
          path={`${pathBase}.title`}
          value={tech.title}
          as="h5"
          multiline
          className="font-serif text-[#F9F8F6] text-2xl md:text-[28px] leading-[1.1] tracking-tight font-semibold"
          testId={`project-${projectIndex + 1}-tech-${techIndex + 1}-title`}
        />
        <EditableText
          path={`${pathBase}.description`}
          value={tech.description}
          as="p"
          multiline
          className="text-[#F9F8F6]/85 text-sm leading-[1.7] mt-10"
          testId={`project-${projectIndex + 1}-tech-${techIndex + 1}-description`}
        />
      </div>
    </article>
  );
}
